// BaseCover.jsx

import React, { useEffect, useState } from 'react';

// Utilities
import { PhotoShape } from 'utilities/Enums';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './BaseCover.css';

function isDirectRenderableImageUrl(url) {
    if (!url || typeof url !== 'string') return false;

    return (
        url.startsWith('blob:') ||
        url.startsWith('data:') ||
        url.startsWith('http://') ||
        url.startsWith('https://') ||
        url.startsWith('/')
    );
}

function getBaseInitials(title) {
    if (!title || typeof title !== 'string') return '?';

    const words = title.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';

    const [firstWord] = words;
    return firstWord.charAt(0).toUpperCase() || '?';
}

function getDimensionValue(value, fallback) {
    if (typeof value === 'number') return `${value}px`;
    if (typeof value === 'string' && value.trim()) return value;
    return `${fallback}px`;
}

function getNumericDimension(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getOptionalDimensionValue(value) {
    if (typeof value === 'number') return `${value}px`;
    if (typeof value === 'string' && value.trim()) return value;
    return null;
}

function getBaseImageSource(base, assetType) {
    if (assetType === 'cover') {
        return typeof base?.sidebarCoverPhotoUrl === 'string'
            ? base.sidebarCoverPhotoUrl.trim()
            : '';
    }

    const iconPhotoUrl = typeof base?.iconPhotoUrl === 'string'
        ? base.iconPhotoUrl.trim()
        : '';

    const sidebarCoverPhotoUrl = typeof base?.sidebarCoverPhotoUrl === 'string'
        ? base.sidebarCoverPhotoUrl.trim()
        : '';

    const coverPhotoUrl = typeof base?.coverPhotoUrl === 'string'
        ? base.coverPhotoUrl.trim()
        : '';

    if (assetType === 'switcher') {
        return iconPhotoUrl || sidebarCoverPhotoUrl || coverPhotoUrl || '';
    }

    if (iconPhotoUrl) {
        return iconPhotoUrl;
    }

    return coverPhotoUrl;
}

/**
 * BaseCover
 *
 * Renders a space cover image when available, or base initials fallback.
 *
 * @param {object} base - Base model containing title and base image fields.
 * @param {number} size - Render size in pixels.
 * @param {string|number} width - Optional rendered width.
 * @param {string|number} height - Optional rendered height.
 * @param {string|number} maxHeight - Optional max height constraint.
 * @param {string} assetType - Image source selector: `icon` or `cover`.
 * @param {number} borderWidth - Container border width in pixels.
 * @param {string|number} borderRadius - Corner radius for square shapes.
 * @param {function} onClick - Optional click handler for the cover container.
 * @param {string} shape - Display shape from PhotoShape enum.
 *
 * @returns {ReactNode} The rendered space cover.
 */
const BaseCover = React.memo(({
    base,
    size = 40,
    width = null,
    height = null,
    maxHeight = null,
    assetType = 'icon',
    borderWidth = 2,
    borderRadius = '7px',
    onClick,
    shape = PhotoShape.CIRCLE,
}) => {

    // ----- STATE ----- //

    // Resolved Image URL State

    const [resolvedImageUrl, setResolvedImageUrl] = useState(null);

    // Image Loaded State
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    // Image Error State
    const [hasImageError, setHasImageError] = useState(false);

    // Normalized persisted base image path.
    const imageSourceUrl = getBaseImageSource(base, assetType);

    // Effective container/image radius based on selected shape.
    const radius = shape === PhotoShape.CIRCLE ? '50%' : borderRadius;

    // Resolved dimensions support square and banner-style renders.
    const resolvedWidth = getDimensionValue(width, size);
    const resolvedHeight = getDimensionValue(height, size);
    const resolvedMaxHeight = getOptionalDimensionValue(maxHeight);

    // Keep fallback initials sized from the smallest explicit numeric dimension.
    const numericWidth = getNumericDimension(width);
    const numericHeight = getNumericDimension(height);
    const explicitDimensions = [numericWidth, numericHeight].filter((value) => value !== null);
    const initialSizeBasis = explicitDimensions.length > 0
        ? Math.min(...explicitDimensions)
        : size;

    // Base initials fallback for missing covers.
    const initials = getBaseInitials(base?.title);
    const altText = assetType === 'cover'
        ? (base?.title || 'Cover')
        : (base?.title || 'Icon');

    // ----- EFFECTS ----- //

    // Resolves storage paths to downloadable URLs when needed.

    useEffect(() => {
        let cancelled = false;

        setIsImageLoaded(false);
        setHasImageError(false);

        if (!imageSourceUrl) {
            setResolvedImageUrl(null);
            return undefined;
        }

        const raw = imageSourceUrl;

        if (isDirectRenderableImageUrl(raw)) {
            setResolvedImageUrl(raw);
            return undefined;
        }

        (async () => {
            try {
                const url = await resolveImageUrl({
                    baseKey: base?.key || base?.id || null,
                    url: raw,
                });
                if (!cancelled) setResolvedImageUrl(url || null);
            } catch {
                if (!cancelled) setResolvedImageUrl(null);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [imageSourceUrl, base?.key, base?.id]);
    if (!base) return null;

    const canRenderImage = Boolean(imageSourceUrl && resolvedImageUrl && !hasImageError);
    const showPlaceholder = Boolean(imageSourceUrl) && !hasImageError && !isImageLoaded;
    const showInitials = !imageSourceUrl || hasImageError;

    // ----- RENDER ----- //

    return (
        <div
          className="base-cover-container"
          style={{
                borderRadius: radius,
                borderWidth: `${borderWidth}px`,
                height: resolvedHeight,
                maxHeight: resolvedMaxHeight || undefined,
                width: resolvedWidth,
            }}
          onClick={onClick}
        >

            {/* SHIMMER */}
            {showPlaceholder && (
                <div
                  className="base-cover-shimmer"
                  style={{
                    borderRadius: radius,
                  }}
                />
            )}

            {/* IMAGE */}
            {canRenderImage && (
                <img
                  className="base-cover-image"
                  style={{
                        borderRadius: radius,
                        opacity: isImageLoaded ? 1 : 0,
                    }}
                  alt={altText}
                  src={resolvedImageUrl}
                  onError={() => {
                        setHasImageError(true);
                        setIsImageLoaded(false);
                    }}
                  onLoad={() => setIsImageLoaded(true)}
                />
            )}

            {/* INITIAL */}
            {showInitials && (
                <div
                  className="base-cover-initial"
                  style={{
                        '--dot-font-size': `${initialSizeBasis * 0.5}px`,
                        '--dot-height': resolvedHeight,
                        '--dot-size': `${size}px`,
                        '--dot-width': resolvedWidth,
                        borderRadius: radius,
                    }}
                >
                    {initials}
                </div>
            )}
        </div>
    );
});

// Dev Tools
BaseCover.displayName = 'BaseCover';

export default BaseCover;
